import { useCallback } from 'react';

import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import { filterCommonAttributes } from '../../../../ai-answer-dialog/analytics-helpers';
import type { commonAttributesType } from '../../../../ai-answer-dialog/types';

export const SAIN_USAGE_DWELL_THRESHOLD = 3000;

export enum SainAction {
	SOURCE_CLICKED = 'sourceLinkClicked',
	POSITIVE_FEEDBACK_PROVIDED = 'positiveFeedbackProvided',
	NEGATIVE_FEEDBACK_PROVIDED = 'negativeFeedbackProvided',
	DWELL_REACHED = 'dwellTimeReached',
	COPY_CLICKED = 'copyButtonClicked',
	FOLLOWUP_CLICKED = 'followupLinkClicked',
	OPEN_CHAT_CLICKED = 'openChatButtonClicked',
	EXPAND_CLICKED = 'expandButtonClicked',
	TOPIC_CLICKED = 'topicClicked',
	RECENT_WORK_CLICKED = 'recentWorkClicked',
	COLLABORATOR_CLICKED = 'collaboratorClicked',
	VIEW_PROFILE_CLICKED = 'viewProfileClicked',
	TEAM_MEMBER_CLICKED = 'teamMemberClicked',
	VIEW_TEAM_PAGE_CLICKED = 'viewTeamPageClicked',
	REPORTING_LINES_CLICKED = 'reportingLinesClicked',
	PERSON_SELECTED = 'cardClicked',
	BOOKMARK_CLICKED = 'bookmarkClicked',
	CHAT_CLICKED = 'chatClicked',
	JIRA_ISSUE_CLICKED = 'jiraIssueCardClicked',
	SHOW_ON_GOOGLE_MAPS_CLICKED = 'showOnGoogleMapsClicked',
}

export type SAINUsageEventPayload = {
	metCriteria?: SainAction;
	dwellTime?: number;
	commonAttributes?: commonAttributesType;
};

export const useSAINUsageCriteriaMet = () => {
	const { trackAIResultView, trackAIResultAction, trackAIFeedbackSubmit } =
		useAIEventsInstrumentation();

	const fireSAINUsageEvent = useCallback(
		({ metCriteria, dwellTime, commonAttributes }: SAINUsageEventPayload) => {
			const sanitizedCommonAttributes = commonAttributes
				? filterCommonAttributes(commonAttributes)
				: {};

			if (metCriteria) {
				// If usage criteria are provided, emit a viewed event with doesNotMeetMAUCriteria set to false
				trackAIResultView({
					doesNotMeetMAUCriteria: false,
					attributes: {
						dwellTime: dwellTime || 0,
						mauEligibilityCriteria: metCriteria,
						...sanitizedCommonAttributes,
					},
				});
				// In addition, emit an action/feedback event for the action that was met
				// (https://hello.atlassian.net/wiki/x/Vxb5DAE)
				switch (metCriteria) {
					case SainAction.SOURCE_CLICKED:
					case SainAction.COPY_CLICKED:
					case SainAction.FOLLOWUP_CLICKED:
					case SainAction.TOPIC_CLICKED:
					case SainAction.RECENT_WORK_CLICKED:
					case SainAction.COLLABORATOR_CLICKED:
					case SainAction.VIEW_PROFILE_CLICKED:
					case SainAction.TEAM_MEMBER_CLICKED:
					case SainAction.VIEW_TEAM_PAGE_CLICKED:
					case SainAction.OPEN_CHAT_CLICKED:
						trackAIResultAction(metCriteria, {
							attributes: { dwellTime, ...sanitizedCommonAttributes },
						});
						break;
					case SainAction.POSITIVE_FEEDBACK_PROVIDED:
						trackAIFeedbackSubmit('up', {
							attributes: { dwellTime, ...sanitizedCommonAttributes },
						});
						break;
					case SainAction.NEGATIVE_FEEDBACK_PROVIDED:
						trackAIFeedbackSubmit('down', {
							attributes: { dwellTime, ...sanitizedCommonAttributes },
						});
						break;
					default:
						break;
				}
			} else {
				// If no usage criteria are provided, emit a viewed event with doesNotMeetMAUCriteria set to true
				trackAIResultView({
					doesNotMeetMAUCriteria: true,
					attributes: {
						dwellTime: dwellTime || 0,
						...sanitizedCommonAttributes,
					},
				});
			}
		},

		[trackAIResultView, trackAIResultAction, trackAIFeedbackSubmit],
	);

	return { fireSAINUsageEvent };
};
